import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Package, Loader2, Radio, MapPin, Calculator, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/utils';

const FEATURES = [
  { icon: Radio, labelKey: 'auth.featureRfid', descKey: 'auth.featureRfidDesc' },
  { icon: MapPin, labelKey: 'auth.featureGeo', descKey: 'auth.featureGeoDesc' },
  { icon: Calculator, labelKey: 'auth.featureNiif', descKey: 'auth.featureNiifDesc' },
  { icon: Shield, labelKey: 'auth.featureAudit', descKey: 'auth.featureAuditDesc' },
];

export function LoginPage() {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [shakeError, setShakeError] = useState(false);
  const { login, loginError, isLoginLoading } = useAuth();

  const loginSchema = z.object({
    email: z
      .string()
      .min(1, t('validation.emailRequired'))
      .email(t('validation.emailInvalid')),
    password: z
      .string()
      .min(1, t('validation.passwordRequired'))
      .min(8, t('validation.passwordMinLogin')),
    totpCode: z.string().optional(),
  });

  type LoginFormData = z.infer<typeof loginSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      totpCode: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      const response = await login(data);
      if (response?.requiresTwoFactor) {
        setRequiresTwoFactor(true);
      }
    } catch {
      setShakeError(true);
      setTimeout(() => setShakeError(false), 500);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left panel - branding with gradient mesh */}
      <div className="hidden lg:flex lg:w-[55%] gradient-mesh relative overflow-hidden">
        {/* Floating orbs for atmosphere */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-16 h-64 w-64 rounded-full bg-white/5 blur-3xl animate-pulse-glow" />
          <div className="absolute bottom-32 right-20 h-48 w-48 rounded-full bg-white/5 blur-3xl animate-pulse-glow" style={{ animationDelay: '1.5s' }} />
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Top - Logo */}
          <div className="opacity-0 animate-fade-in-up stagger-1">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 backdrop-blur-sm border border-white/10">
                <Package className="h-6 w-6" aria-hidden="true" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight">Asset Track 360</h1>
                <p className="text-sm text-white/60 font-mono">EAM Platform</p>
              </div>
            </div>
          </div>

          {/* Center - Hero */}
          <div className="max-w-lg">
            <h2 className="text-5xl font-bold leading-tight tracking-tight opacity-0 animate-fade-in-up stagger-2">
              {t('auth.heroTitle')}
              <span className="block text-white/70">{t('auth.heroTitleHighlight')}</span>
            </h2>
            <p className="mt-4 text-lg text-white/60 leading-relaxed opacity-0 animate-fade-in-up stagger-3">
              {t('auth.heroSubtitle')}
            </p>

            {/* Features - staggered */}
            <div className="mt-10 space-y-4">
              {FEATURES.map((feat, i) => (
                <div
                  key={feat.labelKey}
                  className={cn(
                    'flex items-start gap-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 p-4 opacity-0 animate-slide-in-right',
                    `stagger-${i + 4}`
                  )}
                >
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white/10">
                    <feat.icon className="h-5 w-5 text-white/80" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{t(feat.labelKey)}</p>
                    <p className="text-xs text-white/50 mt-0.5">{t(feat.descKey)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom - Stats bar */}
          <div className="flex items-center gap-8 opacity-0 animate-fade-in-up stagger-8">
            <div>
              <p className="text-2xl font-bold font-mono">NIIF</p>
              <p className="text-xs text-white/40 uppercase tracking-wider">Compliant</p>
            </div>
            <div className="h-8 w-px bg-white/20" />
            <div>
              <p className="text-2xl font-bold font-mono">24/7</p>
              <p className="text-xs text-white/40 uppercase tracking-wider">{t('auth.monitoring')}</p>
            </div>
            <div className="h-8 w-px bg-white/20" />
            <div>
              <p className="text-sm font-bold font-mono">WM Wireless Mobile</p>
              <p className="text-xs text-white/40 uppercase tracking-wider">Software Vendor</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - login form */}
      <div className="flex w-full items-center justify-center px-6 lg:w-[45%]">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="mb-8 text-center lg:hidden opacity-0 animate-fade-in-up stagger-1">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10">
              <Package className="h-7 w-7 text-primary" aria-hidden="true" />
            </div>
            <h1 className="mt-3 text-2xl font-bold text-primary">Asset Track 360</h1>
          </div>

          <div className="rounded-xl border bg-card p-8 shadow-sm opacity-0 animate-scale-in stagger-2">
            <div className="mb-6">
              <h2 className="text-2xl font-bold tracking-tight">{t('auth.loginTitle')}</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('auth.loginSubtitle')}
              </p>
            </div>

            {loginError && (
              <div
                className={cn(
                  'mb-4 rounded-lg bg-destructive/10 p-3 text-sm text-destructive border border-destructive/20',
                  shakeError && 'animate-[shake_0.4s_ease-in-out]'
                )}
                role="alert"
              >
                {(loginError as Error).message ||
                  t('auth.invalidCredentials')}
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="space-y-5">
                {/* Email */}
                <div className="opacity-0 animate-fade-in-up stagger-3">
                  <label
                    htmlFor="email"
                    className="mb-1.5 block text-sm font-medium"
                  >
                    {t('auth.email')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder={t('auth.emailPlaceholder')}
                    className="h-11 w-full rounded-lg border border-input bg-background px-3 text-sm ring-offset-background transition-all placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-transparent"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                    {...register('email')}
                  />
                  {errors.email && (
                    <p id="email-error" className="mt-1.5 text-xs text-destructive" role="alert">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div className="opacity-0 animate-fade-in-up stagger-4">
                  <label
                    htmlFor="password"
                    className="mb-1.5 block text-sm font-medium"
                  >
                    {t('auth.password')}
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder={t('auth.passwordPlaceholder')}
                      className="h-11 w-full rounded-lg border border-input bg-background px-3 pr-11 text-sm ring-offset-background transition-all placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-transparent"
                      aria-invalid={!!errors.password}
                      aria-describedby={
                        errors.password ? 'password-error' : undefined
                      }
                      {...register('password')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-0 top-0 inline-flex h-11 w-11 items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={
                        showPassword ? t('auth.hidePassword') : t('auth.showPassword')
                      }
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p
                      id="password-error"
                      className="mt-1.5 text-xs text-destructive"
                      role="alert"
                    >
                      {errors.password.message}
                    </p>
                  )}
                </div>

                {/* 2FA Code */}
                {requiresTwoFactor && (
                  <div className="opacity-0 animate-fade-in-up">
                    <label
                      htmlFor="totpCode"
                      className="mb-1.5 block text-sm font-medium"
                    >
                      {t('auth.twoFactorCode')}
                    </label>
                    <input
                      id="totpCode"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="000000"
                      maxLength={6}
                      className="h-11 w-full rounded-lg border border-input bg-background px-3 text-center text-lg font-mono tracking-[0.5em] ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      {...register('totpCode')}
                    />
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      {t('auth.twoFactorHint')}
                    </p>
                  </div>
                )}

                {/* Submit */}
                <div className="opacity-0 animate-fade-in-up stagger-5">
                  <button
                    type="submit"
                    disabled={isLoginLoading}
                    className="inline-flex h-11 w-full items-center justify-center rounded-lg bg-primary text-sm font-semibold text-primary-foreground transition-all hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
                  >
                    {isLoginLoading ? (
                      <>
                        <Loader2
                          className="mr-2 h-4 w-4 animate-spin"
                          aria-hidden="true"
                        />
                        {t('auth.loggingIn')}
                      </>
                    ) : (
                      t('auth.login')
                    )}
                  </button>
                </div>
              </div>
            </form>

            <div className="mt-5 text-center opacity-0 animate-fade-in stagger-6">
              <a
                href="/recuperar-contrasena"
                className="text-sm text-primary hover:underline underline-offset-4"
              >
                {t('auth.forgotPassword')}
              </a>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-muted-foreground opacity-0 animate-fade-in stagger-7">
            &copy; {new Date().getFullYear()} {t('auth.copyright')}
          </p>
        </div>
      </div>
    </div>
  );
}
